import { FC, ReactNode, MouseEvent } from "react";
import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import Plus from "@/icons/__generated__/Plus/Plus20";
import PrivacyOptions14 from "@/icons/__generated__/PrivacyOptionsIcon/PrivacyOptions14";
import PSStack from "@/icons/__generated__/PSStack/PsStack";
import { cn } from "@/lib/utils";

interface FooterLink {
  sectionTitle?: string;
  links?: {
    href?: string;
    text?: string;
    id?: string;
    onClick?: (e: MouseEvent<HTMLElement>, id: string) => void;
  }[];
  id?: string;
  onClick?: (e: MouseEvent<HTMLElement>, id: string) => void;
}

interface PrivacyData {
  privacyOnClick?: () => void;
  privacyText?: string;
}

export interface FooterProps {
  footerLinks?: FooterLink[];
  showLocaleSelector?: boolean;
  privacyData?: PrivacyData;
  footerSocialLinks?: {
    href?: string;
    title?: string;
    icon?: ReactNode;
    id?: string;
    onClick?: (e: MouseEvent<HTMLElement>, id: string) => void;
  }[];
  LocaleSelectorComp?: ReactNode;
  showDownload?: boolean;
  DownloadComp?: ReactNode;
  showTermsAndPrivacy?: boolean;
  TermsAndPrivacyComp?: ReactNode;
  currentLocale?: {
    url_prefix?: string;
    zendesk_url_locale: string;
    country: string;
    google_locale: string;
    language: string;
    region: string;
  };
}

export const Footer: FC<FooterProps> = ({
  footerLinks = [],
  showLocaleSelector = false,
  privacyData,
  footerSocialLinks = [],
  LocaleSelectorComp,
  showDownload = false,
  DownloadComp,
  showTermsAndPrivacy = false,
  TermsAndPrivacyComp,
}: FooterProps) => {
  const plusIcon = <Plus className="tw-text-base-white" />;

  const mobileFooter = () => {
    return (
      <Accordion className="tw-w-full" collapsible type="single">
        {footerLinks.map((item, i) => (
          <AccordionItem
            key={`item-${i}`}
            value={`item-${i}`}
            className="tw-border-b-gray-700"
          >
            <AccordionTrigger
              triggerIcon={plusIcon}
              data-testid={`${item.sectionTitle || ""}-mobile-footer-trigger`}
              id={item.id || ""}
              onClick={(e) => item.onClick && item.onClick(e, item.id || "")}
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                <Text
                  element="p"
                  typeface="body-2"
                  className="tw-text-base-white"
                >
                  {item.sectionTitle || ""}
                </Text>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {item.links?.map((link, index) => {
                return (
                  <div key={`link-${index}`} className="tw-mb-8 tw-pl-16">
                    <a
                      className="tw-body-2 tw-text-gray-500"
                      href={link.href || ""}
                      id={link.id || ""}
                      onClick={(e) =>
                        link.onClick && link.onClick(e, link.id || "")
                      }
                    >
                      {link.text || ""}
                    </a>
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  const subFooter = () => {
    return (
      <div className="lg:tw-flex lg:tw-items-center tw-w-full tw-justify-center tw-mt-40 lg:tw-mt-0">
        <div className="tw-flex tw-items-center lg:tw-flex-1 lg:tw-justify-start tw-justify-center tw-mb-32 lg:tw-mb-0">
          {showLocaleSelector && (
            <div className="tw-text-base-white tw-flex">
              {LocaleSelectorComp}
              <div className="tw-mx-12 tw-text-gray-500">•</div>
            </div>
          )}
          <div>
            <button
              className="btn btn-link tw-text-base-black underline p-0 text-left font-weight-normal flex items-center"
              id="footer_link_cookies"
              onClick={() => {
                if (typeof window !== "undefined") {
                  privacyData?.privacyOnClick?.();
                }
              }}
            >
              <div className="tw-flex tw-items-center">
                <span
                  className="tw-body-2-underline tw-text-base-white tw-mr-8 tw-capitalize"
                  aria-label={privacyData?.privacyText}
                >
                  {privacyData?.privacyText}
                </span>
                <PrivacyOptions14 />
              </div>
            </button>
          </div>
        </div>
        <div className="tw-flex tw-items-center lg:tw-justify-end tw-justify-center">
          {footerSocialLinks.map((link, i) => (
            <a
              key={`desktop-footer-${link.href || ""}-${i}`}
              className={cn("tw-text-gray-500 tw-mx-12", {
                "tw-mr-0": i === footerSocialLinks.length - 1,
              })}
              href={link.href || "#"}
              title={link.title || ""}
              tabIndex={0}
              aria-label={link.title || ""}
              id={link.id || ""}
              onClick={(e) => link.onClick && link.onClick(e, link.id || "")}
            >
              {link.icon || ""}
            </a>
          ))}
        </div>
        <div
          className={cn("lg:tw-hidden tw-flex tw-justify-center", {
            "tw-mt-32": !!footerSocialLinks.length,
          })}
        >
          {peerspaceCopyRight()}
        </div>
      </div>
    );
  };

  const desktopFooter = (showDownload: boolean) => {
    return (
      <>
        <div className="tw-grid tw-grid-cols-12">
          <div className="tw-text-base-white tw-col-span-3">
            <div className="tw-mb-24">
              <PSStack
                width={100}
                height={100}
                className="tw-text-base-white"
              />
            </div>
            <div>
              {showDownload && <>{DownloadComp}</>}
              {showTermsAndPrivacy && <>{TermsAndPrivacyComp}</>}
            </div>
          </div>
          <div className="tw-flex tw-col-span-9">
            {footerLinks.length > 0 &&
              footerLinks.map((category, i) => (
                <div
                  key={`category-${i}`}
                  className={cn("tw-mx-16 tw-flex-1", {
                    "tw-mr-0": i === footerLinks.length - 1,
                  })}
                >
                  <h3 className="tw-text-base-white tw-body-2-m tw-mb-24">
                    {category.sectionTitle || ""}
                  </h3>
                  {category.links?.map((link, index) => (
                    <div
                      key={`link-${index}`}
                      className="tw-mb-8 lg:tw-mb-12 xl:tw-mb-8"
                    >
                      <a
                        href={link.href}
                        className="!tw-leading-16 tw-body-2 lg:tw-body-1 xl:tw-body-2 tw-text-gray-500 tw-hover:underline"
                        tabIndex={0}
                        aria-label={link.text}
                        id={link.id || ""}
                        onClick={(e) =>
                          link.onClick && link.onClick(e, link.id || "")
                        }
                      >
                        {link.text}
                      </a>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
        <div className="tw-mt-40">
          <div className="tw-flex tw-items-center tw-justify-end tw-mb-16">
            {peerspaceCopyRight()}
          </div>
          <div className="tw-w-full tw-mb-32 tw-h-2 tw-bg-gray-500"></div>
        </div>
      </>
    );
  };

  const peerspaceCopyRight = () => {
    return <p className="tw-text-gray-500 tw-mono-2">&#169; Peerspace</p>;
  };

  return (
    <footer
      className="tw-bg-base-black tw-w-full tw-px-40 tw-py-48 lg:tw-px-64 lg:tw-pb-48 max-[390px]:tw-pt-48 max-[390px]:tw-px-16 max-[390px]:tw-pb-80"
      data-testid="footer-component"
    >
      <div
        className="lg:tw-visible lg:tw-block tw-hidden lg:tw-pt-40"
        data-testid="desktop-footer-component"
      >
        {desktopFooter(showDownload)}
      </div>
      <div className="lg:tw-hidden" data-testid="mobile-footer-component">
        {mobileFooter()}
      </div>
      {subFooter()}
    </footer>
  );
};
